import { Box, Dialog, Alert, Chip } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { useRef, useState } from "react";
import { useQuery } from "react-query";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import { useForm } from "react-hook-form";
import {
  getCostEstimateDetails,
  getLandDetailsById,
} from "src/utils/api/createProjectFlow";
import defaultStyles, { StylesClasses } from "./styles";
import AutoComplete from "src/components/common/FormComponents/AutoComplete";
import errorMessage from "src/constants/errorMessage";
import { getStyles } from "src/styles/theme";
import { DashBoardTable } from "src/components/common/DashboardTable/costEstimateDetails";
import ViewForm from "src/components/common/ViewForm";
import { formatToIndianCurrency } from "src/utils/helper";
import SuccessModal from "src/components/common/modal/Success";
import EcoRestorationProgress from "../DemandNoteViewProgress";
import { CostEstimateSummary } from "src/components/common/DashboardTable/costEstimateSummary";
import ViewImages from "src/components/common/ViewImage";
import { useReactToPrint } from "react-to-print";
import { SeedlingTable } from "src/components/common/DashboardTable/seedlingTable";
import { UpdateProgress } from "src/components/common/DashboardTable/updateProgress";
import { AddProgress } from "./EditCostEstimateForm";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "src/redux/store";
import { TargetQueryHistoryModal } from "./TargetQueryHistoryModal";
import { SeedlingQueryHistoryModal } from "./SeedlingQueryHistoryModal";
import { UpdateSeedlingModal } from "./UpdateSeedlingModal";

const AdvanceWorkProgress = () => {
  const componentRef = useRef();

  const [searchParams] = useSearchParams();

  const landOwnershipEpid = searchParams.get("id");

  const user = useAppSelector((state) => state.root.user);

  const styles = getStyles<StylesClasses>(defaultStyles);

  const [yearTableRows, setYearTableRows] = useState({});

  const [yearSummaryTableRows, setYearSummaryTableRows] = useState([]);
  const [seedlingTableData, setSeedlingTableData] = useState([]);
  const [totalPendingQueries, setTotalPendingQueries] = useState(0);
  const totalPendingSeedlingQueries = seedlingTableData.reduce(
    (total, seedling: any) => total + Number(seedling?.pendingQueryCount || 0),
    0
  );

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [rowToDelete, setRowToDelete] = useState(null);

  const [showForm, setShowForm] = useState(false);

  const [queryModalState, setQueryModalState] = useState<{
    open: boolean;
    targetId: string | null;
  }>({
    open: false,
    targetId: null,
  });

  const [seedlingModalState, setSeedlingModalState] = useState<{
    open: boolean;
    seedling: any | null;
  }>({
    open: false,
    seedling: null,
  });

  const [seedlingQueryModalState, setSeedlingQueryModalState] = useState<{
    open: boolean;
    seedlingRecordId: string | null;
    seedlingName: string;
  }>({
    open: false,
    seedlingRecordId: null,
    seedlingName: "",
  });

  const handleOpenSeedlingQueryModal = (seedling: any) => {
    setSeedlingQueryModalState({
      open: true,
      seedlingRecordId: seedling.id,
      seedlingName: seedling.commonName || "",
    });
  };

  const handleCloseSeedlingQueryModal = () => {
    setSeedlingQueryModalState({
      open: false,
      seedlingRecordId: null,
      seedlingName: "",
    });
    refetch();
  };

  const { data: userData } = useQuery(
    ["userDetails", landOwnershipEpid],
    () => getLandDetailsById(landOwnershipEpid),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (req: AxiosError) => {},
      enabled: !!landOwnershipEpid,
      refetchOnMount: true,
    }
  );

  const mapYearData = (yearTargets) => {
    return yearTargets?.map((row) => ({
      data: row,
      id: row.id,
      sno: row.serialNumber,
      year: row?.year,
      costEstimateID: row?.landOwnershipEpid,
      mainComponent: row?.indicator?.name,
      subComponent: row?.activity?.name,
      perticularOfItem: row?.perticularOfItem,
      unit: row?.unit,
      costPerUnit: row?.costPerUnit,
      numberOfUnits: row?.numberOfUnits,
      costRequired: row?.costRequired,
      progressInUnits: row?.progressInUnits,
      costProgress: row?.costProgress,
      status: row?.status,
    }));
  };

  const { data: targetData, refetch } = useQuery(
    ["costEstimateDetails", landOwnershipEpid],
    () =>
      getCostEstimateDetails({
        landOwnershipEpid,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const yearData = {
          firstYear: mapYearData(res?.data?.data?.firstYearTargets),
          secondYear: mapYearData(res?.data?.data?.secondYearTargets),
          thirdYear: mapYearData(res?.data?.data?.thirdYearTargets),
          fourthYear: mapYearData(res?.data?.data?.fourthYearTargets),
          fifthYear: mapYearData(res?.data?.data?.fifthYearTargets),
          sixthYear: mapYearData(res?.data?.data?.sixthYearTargets),
          seventhYear: mapYearData(res?.data?.data?.seventhYearTargets),
          eighthYear: mapYearData(res?.data?.data?.eighthYearTargets),
          ninthYear: mapYearData(res?.data?.data?.ninthYearTargets),
          tenthYear: mapYearData(res?.data?.data?.tenthYearTargets),
          other: mapYearData(res?.data?.data?.otherNecessaryProvisions),
        };
        const sumCostProgress = (targets: any[]) =>
          (targets || []).reduce(
            (acc, t) => acc + Number(t?.costProgress || 0),
            0
          );

        setYearSummaryTableRows([
          {
            costRequired: res?.data?.data?.firstYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.firstYearTargets),
          },
          {
            costRequired: res?.data?.data?.secondYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.secondYearTargets),
          },
          {
            costRequired: res?.data?.data?.thirdYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.thirdYearTargets),
          },
          {
            costRequired: res?.data?.data?.fourthYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.fourthYearTargets),
          },
          {
            costRequired: res?.data?.data?.fifthYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.fifthYearTargets),
          },
          {
            costRequired: res?.data?.data?.sixthYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.sixthYearTargets),
          },
          {
            costRequired: res?.data?.data?.seventhYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.seventhYearTargets),
          },
          {
            costRequired: res?.data?.data?.eighthYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.eighthYearTargets),
          },
          {
            costRequired: res?.data?.data?.ninthYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.ninthYearTargets),
          },
          {
            costRequired: res?.data?.data?.tenthYearTotalCostRequired,
            costProgress: sumCostProgress(res?.data?.data?.tenthYearTargets),
          },
          {
            costRequired:
              res?.data?.data?.otherNecessaryProvisionsTotalCostRequired,
            costProgress: sumCostProgress(
              res?.data?.data?.otherNecessaryProvisions
            ),
          },
        ]);
        setSeedlingTableData(res?.data?.data?.seedlings);
        setYearTableRows(yearData);

        // Calculate total pending queries
        let totalPending = 0;
        Object.values(yearData).forEach((yearTargets: any) => {
          if (Array.isArray(yearTargets)) {
            yearTargets.forEach((target: any) => {
              totalPending += target?.data?.pendingQueryCount || 0;
            });
          }
        });
        setTotalPendingQueries(totalPending);
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  const {
    control,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      landOwnershipEpid: "",
    },
  });

  const commonTableHeaders = [
    "Year",
    "Main Component",
    "Sub Component",
    "Particulars of item of works",
    "Unit",
    "No. of Units Required",
    "Cost Required(Rs.)",
    "Physical Progress",
    "Financial Progress",
    "Mandays Generated",
  ];

  const commonSummaryHeaders = ["Year", "Cost Required", "Financial Progress"];
  const commonSeedlingHeader = [
    "Common Name",
    "Botanical Name",
    "No of Proposed Seedlings",
    "Planted Seedlings",
  ];

  const [RowData, setRowData] = useState();
  const [editFormState, setEditFormState] = useState<boolean>(false);
  const handleCostEstimateTableEdit = (row) => {
    setEditFormState(true);
    setRowData(row);
  };

  const handleOpenQueryModal = (row) => {
    setQueryModalState({
      open: true,
      targetId: row.id,
    });
  };

  const handleCloseQueryModal = () => {
    setQueryModalState({
      open: false,
      targetId: null,
    });
    // Refetch to update pending query counts
    refetch();
  };

  const handleOpenSeedlingModal = (seedling: any) => {
    if (
      user?.data?.role !== "Implementing Agency" ||
      user?.data?.userType !== null
    )
      return;
    const isFirstTime =
      !seedling?.fromDate &&
      !seedling?.toDate &&
      (!seedling?.numberOfPlantedSeedlings ||
        Number(seedling?.numberOfPlantedSeedlings) === 0);
    const isQueryPending = (seedling?.pendingQueryCount || 0) > 0;

    if (!(seedling.canUpdateDetails ?? (isQueryPending || isFirstTime))) return;

    setSeedlingModalState({
      open: true,
      seedling,
    });
  };

  const handleCloseSeedlingModal = () => {
    setSeedlingModalState({
      open: false,
      seedling: null,
    });
    refetch();
  };

  const renderYearlyTables = () => {
    return Object.keys(yearTableRows).map((yearKey, index) => {
      if (yearKey === "yearSummary") return;
      const tableData: DashBoardTable = {
        topHeader: commonTableHeaders,
        data: yearTableRows[yearKey],
      };

      if (yearTableRows[yearKey]?.length > 0) {
        return (
          <Box key={yearKey} sx={{ marginBottom: "30px", marginTop: "30px" }}>
            <UpdateProgress
              {...tableData}
              onEdit={handleCostEstimateTableEdit}
              onQueryClick={handleOpenQueryModal}
              status={targetData?.data?.data?.status}
            />
          </Box>
        );
      }
      return null;
    });
  };

  const renderSummaryTable = () => {
    const summaryData: DashBoardTable = {
      topHeader: commonSummaryHeaders,
      data: yearSummaryTableRows,
    };
    const seedlingData: DashBoardTable = {
      topHeader: commonSeedlingHeader,
      data: seedlingTableData,
    };

    if (yearSummaryTableRows) {
      return (
        <Box
          sx={{
            marginBottom: "30px",
            marginTop: "30px",
            display: "flex",
            gap: "20px",
          }}
        >
          <CostEstimateSummary
            {...summaryData}
            onEdit={handleCostEstimateTableEdit}
            status={targetData?.data?.data?.status}
          />
          <SeedlingTable
            {...seedlingData}
            editable
            onUpdateDetails={handleOpenSeedlingModal}
            onQueryClick={handleOpenSeedlingQueryModal}
          />
        </Box>
      );
    }
  };

  const bottomRef = useRef(null);

  const moveToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleRedirect = () => {
    moveToBottom();
    setShowForm(true);
  };

  const handleAddForm = () => {
    setShowForm(false);
    refetch();
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@media print { body { -webkit-print-color-adjust: exact; } }",
  });

  const epiddata = userData?.data;

  return (
    <>
      <DashboardWrapper
        title={`${
          user?.data?.userType === null
            ? "Update Progress Details"
            : "Progress Details"
        }`}
        buttonText="Print"
        onButtonClick={handlePrint}
        showBtn={true}
        showBackBtn={true}
      >
        <>
          <Box>
            <Box ref={componentRef} id="printable-content">
              <Box
                sx={{
                  fontSize: "22px",
                  textAlign: "center",
                  marginTop: "26px",
                  fontWeight: "bold",
                }}
              >
                Cost Estimate for Eco-restoration of Degraded Forest Lands under
                the Green Credit Programme
              </Box>
              {(totalPendingQueries > 0 || totalPendingSeedlingQueries > 0) && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                    marginTop: "16px",
                    marginBottom: "16px",
                  }}
                >
                  {[
                    { type: "progress", count: totalPendingQueries },
                    { type: "seedling", count: totalPendingSeedlingQueries },
                  ]
                    .filter(({ count }) => count > 0)
                    .map(({ type, count }) => (
                      <Alert
                        key={type}
                        severity="warning"
                        icon={false}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "16px",
                          fontWeight: "500",
                        }}
                      >
                        <Chip
                          label={count}
                          color="error"
                          size="small"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "14px",
                            height: "28px",
                            margin: "4px",
                          }}
                        />
                        Pending {type} {count === 1 ? "query" : "queries"} in
                        review
                      </Alert>
                    ))}
                </Box>
              )}
              <Box {...styles("formWrapper")}>
                <Box sx={{ width: "40%" }}>
                  <ViewForm
                    label={"Registration No"}
                    value={epiddata?.projectDetail?.registrationNo}
                  />
                  <ViewForm label={"EPID"} value={epiddata?.id} />
                  <ViewForm
                    label={"State"}
                    value={epiddata?.projectDetail?.address?.stateDetail?.name}
                  />
                  <ViewForm
                    label={"District"}
                    value={
                      epiddata?.projectDetail?.address?.districtDetail?.name
                    }
                  />
                  <ViewForm
                    label={"Division"}
                    value={epiddata?.projectDetail?.landSpecification?.division}
                  />
                  <ViewForm
                    label={"Range"}
                    value={epiddata?.projectDetail?.landSpecification?.range}
                  />
                  <ViewForm
                    label={"Beat"}
                    value={epiddata?.projectDetail?.landSpecification?.beat}
                  />
                </Box>

                <Box sx={{ width: "40%" }}>
                  <ViewForm
                    label={"Total Area(ha)"}
                    value={
                      epiddata?.projectDetail?.landSpecification?.totalArea
                    }
                  />
                  <ViewForm
                    label={"Net Plantation Area(ha)"}
                    value={epiddata?.netPlantationArea}
                  />
                  <ViewForm
                    label={"Eco-Restoration Cost"}
                    value={formatToIndianCurrency(epiddata?.demandNote)}
                  />
                  {targetData?.data?.data?.plantationPlanning?.pitSize && (
                    <>
                      <ViewForm
                        label={"Sapling spacing(m X m)"}
                        value={`${targetData?.data?.data?.plantationPlanning?.treeSeedlingPacingX} X ${targetData?.data?.data?.plantationPlanning?.treeSeedlingPacingY}`}
                      />
                      <ViewForm
                        label={"Pit Size"}
                        value={
                          targetData?.data?.data?.plantationPlanning?.pitSize
                        }
                      />
                      <ViewForm
                        label={"Total No. of Saplings"}
                        value={
                          targetData?.data?.data?.plantationPlanning
                            ?.totalNumberOfSeedlingToBePlanted
                        }
                      />
                    </>
                  )}
                </Box>

                <Box sx={{ width: "30%" }}>
                  <Box
                    sx={{
                      fontSize: "17px",
                      fontWeight: "bold",
                    }}
                  >
                    Technically sanctioned/approved cost estimate
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "20px",
                    }}
                  >
                    <Box {...styles("documentGroup")}>
                      <ViewImages
                        fileUrl={epiddata?.paymentBreakdownPdfImage}
                        label={"PDF"}
                      />
                    </Box>
                    <Box {...styles("documentGroup")}>
                      <ViewImages
                        fileUrl={epiddata?.paymentBreakdownImage}
                        label={"Ms Excel"}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              {Object.values(yearTableRows).flat().length > 0 ? (
                <>
                  {/* Render all year tables */}
                  {renderSummaryTable()}
                  {renderYearlyTables()}
                </>
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </>

        <Dialog
          open={editFormState}
          onClose={() => {
            setEditFormState(false);
            refetch();
          }}
          fullWidth
          maxWidth="lg"
          sx={{
            "& .MuiDialog-paper": {
              width: "100%",
              borderRadius: "16px",
            },
          }}
        >
          <AddProgress
            rowData={RowData}
            selfState={setEditFormState}
            refetch={refetch}
          />
        </Dialog>
        <Dialog
          open={queryModalState.open}
          onClose={handleCloseQueryModal}
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              width: "100%",
              borderRadius: "16px",
            },
          }}
        >
          {queryModalState.targetId && (
            <TargetQueryHistoryModal
              targetId={queryModalState.targetId}
              open={queryModalState.open}
              onClose={handleCloseQueryModal}
            />
          )}
        </Dialog>
        <Dialog
          open={seedlingModalState.open}
          onClose={handleCloseSeedlingModal}
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              width: "100%",
              borderRadius: "16px",
            },
          }}
        >
          {seedlingModalState.seedling && (
            <UpdateSeedlingModal
              seedling={seedlingModalState.seedling}
              onClose={handleCloseSeedlingModal}
            />
          )}
        </Dialog>
        <Dialog
          open={seedlingQueryModalState.open}
          onClose={handleCloseSeedlingQueryModal}
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              width: "100%",
              borderRadius: "16px",
            },
          }}
        >
          {seedlingQueryModalState.seedlingRecordId && (
            <SeedlingQueryHistoryModal
              seedlingRecordId={seedlingQueryModalState.seedlingRecordId}
              open={seedlingQueryModalState.open}
              onClose={handleCloseSeedlingQueryModal}
              seedlingName={seedlingQueryModalState.seedlingName}
            />
          )}
        </Dialog>
      </DashboardWrapper>
    </>
  );
};

export default AdvanceWorkProgress;
